import { useCallback, useEffect, useState } from 'react'

// Per-browser rich-text edits of the voiceover texts (the speaker may format
// the text for easier reading). Same storage model as done/notes: localStorage
// keyed by mode id. Map of file name -> edited HTML. Empty = use the original.
const PREFIX = 'voiceover-textedits:v1:'

export type TextEditsState = {
  get: (id: string) => string
  set: (id: string, html: string) => void
  has: (id: string) => boolean
}

export function useTextEdits (storageKey: string): TextEditsState {
  const key = PREFIX + storageKey
  const [edits, setEdits] = useState<Record<string, string>>({})

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(key)
      setEdits(raw ? JSON.parse(raw) : {})
    } catch {
      setEdits({})
    }
  }, [key])

  const set = useCallback(
    (id: string, html: string) => {
      setEdits((prev) => {
        const next = { ...prev }
        if (!html || html.trim().length === 0) {
          delete next[id]
        } else {
          next[id] = html
        }
        try {
          window.localStorage.setItem(key, JSON.stringify(next))
        } catch {
          /* ignore quota / disabled storage */
        }
        return next
      })
    },
    [key]
  )

  const get = useCallback((id: string) => edits[id] ?? '', [edits])
  const has = useCallback((id: string) => (edits[id] ?? '').length > 0, [edits])

  return { get, set, has }
}
